import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CompanyService } from '../_services/company.service';
import { AuthorizationService } from '../_services/authorization.service';
import { LogoutService } from '../_services/logout.service';
import { TranslateService } from '@ngx-translate/core';
import { LoginResult } from '../_interface/apiResponse';

@Component({
    selector: 'app-pin-login',
    templateUrl: "./pin-login.component.html",
    styleUrls: ['./pin-login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PinLoginComponent {
  isBusy = false;

  pin: string = '';

  @Input() pinConfig: any;

  @Output() onLogedIn = new EventEmitter<LoginResult>();
  @Output() onLogout = new EventEmitter();

  constructor(private authService: AuthorizationService,
    private logoutService: LogoutService,
    private translate: TranslateService,
    private dialog: MatDialog) {

  }

  confirmLogout() {
    // PIN login is a waiter-app feature, so its specific wording + translations
    // live in the waiter app i18n. Resolve it here and pass the loaded text to
    // the shared (generic) LogoutService — not a translation key.
    this.logoutService.confirmLogout(this.translate.instant('logoutConfirmationTextPin')).then((confirmed) => {
      if (confirmed) {
        this.logout();
      }
    });
  }

  // keydown, ne keypress: Backspace ne okida keypress, pa brisanje sa tastature nije radilo.
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    var validkeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

    // Dok je otvoren dijalog (poruka o grešci, potvrda odjave) tastatura pripada njemu.
    // Ranije je Enter nad porukom ponovo slao prijavu (sa praznim PIN-om) i otvarao
    // novu poruku preko stare, a cifre su se upisivale u PIN iza dijaloga.
    if (this.dialog.openDialogs.length > 0 || event.defaultPrevented) {
      return;
    }

    // Prečice (Ctrl+R, Alt+...) i unos u neko polje nisu PIN.
    if (event.ctrlKey || event.altKey || event.metaKey || event.isComposing || isTextField(event.target)) {
      return;
    }

    if (event.key === 'Enter') {
      // Bez nativnog klika na dugme koje je u fokusu (npr. posle klika mišem na cifru).
      event.preventDefault();
      this.login();
    }
    else if (event.key === 'Backspace') {
      event.preventDefault();
      this.delete();
    }
    else if (validkeys.indexOf(event.key) > -1) {
      this.addPin(event.key);
    }
  }

  addPin(char: string | number) {
    this.pin += char;

  }

  delete() {
    this.pin = this.pin.slice(0, -1);
  }

  login() {
    // Bez duple prijave dok zahtev traje i bez slanja praznog PIN-a
    // (server ga odbija greškom koju klijent prikaže kao "nalog zaključan").
    if (this.isBusy || !this.pin) {
      return;
    }

    this.isBusy = true;
    this.authService.loginWithPin(this.pinConfig.companyID, this.pin, this.pinConfig.token).subscribe((res) => {
      this.onLogedIn.next(res);
    }).add(() => {
      this.pin = '';
      this.isBusy = false;
    });
  }

  logout() {
    this.onLogout.emit();
  }

}

function isTextField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}
